import { z } from 'zod';
const wnlMessage = { message: 'Mark as within normal limits if none applies', path: [] };
const baseMentalStatus = z.object({
    wnl: z.boolean().optional(),
    orientedToPerson: z.string().optional(),
    orientedToPlace: z.string().optional(),
    orientedToPurpose: z.string().optional(),
    orientedToTime: z.string().optional(),
});
const baseCognitiveBehavioral = z.object({
    wnl: z.boolean().optional(),
    confused: z.boolean().optional(),
    lethargic: z.boolean().optional(),
    forgetful: z.boolean().optional(),
    aphasiac: z.boolean().optional(),
    aphasiaDetails: z.array(z.string()).optional(),
    dizziness: z.boolean().optional(),
    headache: z.boolean().optional(),
    lightheaded: z.boolean().optional(),
});
const baseNeurologicalFindings = z.object({
    wnl: z.boolean().optional(),
    details: z.array(z.string()).optional(),
    neuroImpactsADLs: z.boolean().optional(),
});
const baseHandGrips = z.object({
    wnl: z.boolean().optional(),
    dominantHand: z.string().optional(),
    equality: z.string().optional(),
    leftStrength: z.string().optional(),
    rightStrength: z.string().optional(),
});
const baseExtremities = z.object({
    wnl: z.boolean().optional(),
    lue: z.string().optional(),
    rue: z.string().optional(),
    lle: z.string().optional(),
    rle: z.string().optional(),
});
const baseComprehensionLanguage = z.object({
    wnl: z.boolean().optional(),
    details: z.array(z.string()).optional(),
});
export const baseVisionSchema = z.object({
    wnl: z.boolean().optional(),
    correctiveLenses: z.string().optional(),
    visionSeverity: z.string().optional(),
    leftEye: z.string().optional(),
    rightEye: z.string().optional(),
    visualFieldDefect: z.string().optional(),
    impactOnCare: z.array(z.string()).default([]),
});
export const baseHearingSchema = z.object({
    wnl: z.boolean().optional(),
    hearingAidStatus: z.string().optional(),
    hearingAbility: z.string().optional(),
    leftEar: z.string().optional(),
    rightEar: z.string().optional(),
    comprehension: z.string().optional(),
    interpreterNeeded: z.string().optional(),
    impactOnCare: z.array(z.string()).default([]),
});
export const visionRefined = baseVisionSchema.refine((d) => d.wnl || d.correctiveLenses || d.visionSeverity || d.leftEye || d.rightEye || d.visualFieldDefect || !!d.impactOnCare?.length, wnlMessage);
export const hearingRefined = baseHearingSchema.refine((d) => d.wnl ||
    d.hearingAidStatus ||
    d.hearingAbility ||
    d.leftEar ||
    d.rightEar ||
    d.comprehension ||
    d.interpreterNeeded ||
    !!d.impactOnCare?.length, wnlMessage);
export const baseNeurologicalSchemaRefined = z.object({
    mentalStatus: baseMentalStatus.refine((d) => d.wnl || d.orientedToPerson || d.orientedToPlace || d.orientedToPurpose || d.orientedToTime, wnlMessage),
    cognitiveBehavioral: baseCognitiveBehavioral.refine((d) => d.wnl || d.confused || d.lethargic || d.forgetful || d.aphasiac || d.dizziness || d.headache || d.lightheaded, wnlMessage),
    neurologicalFindings: baseNeurologicalFindings.refine((d) => d.wnl || !!d.details?.length || d.neuroImpactsADLs, wnlMessage),
    handGrips: baseHandGrips.refine((d) => d.wnl || d.dominantHand || d.equality || d.leftStrength || d.rightStrength, wnlMessage),
    extremities: baseExtremities.refine((d) => d.wnl || d.lue || d.rue || d.lle || d.rle, wnlMessage),
    comprehensionLanguage: baseComprehensionLanguage.refine((d) => d.wnl || !!d.details?.length, wnlMessage),
    pupils: z
        .object({
        wnl: z.boolean().optional(),
        equality: z.string().optional(),
        shape: z.string().optional(),
        size: z.string().optional(),
        leftReactivity: z.string().optional(),
        rightReactivity: z.string().optional(),
    })
        .refine((d) => d.wnl || d.equality || d.shape || d.size || d.leftReactivity || d.rightReactivity, wnlMessage),
    vision: visionRefined.optional(),
    hearing: hearingRefined.optional(),
    entMouth: z
        .object({
        wnl: z.boolean().optional(),
        details: z.array(z.string()).optional(),
    })
        .refine((d) => d.wnl || !!d.details?.length, wnlMessage)
        .optional(),
});
export const baseNeurologicalSchema = z.object({
    mentalStatus: baseMentalStatus,
    cognitiveBehavioral: baseCognitiveBehavioral,
    neurologicalFindings: baseNeurologicalFindings,
    handGrips: baseHandGrips,
    extremities: baseExtremities,
    comprehensionLanguage: baseComprehensionLanguage,
    pupils: z
        .object({
        wnl: z.boolean().optional(),
        equality: z.string().optional(),
        shape: z.string().optional(),
        size: z.string().optional(),
        leftReactivity: z.string().optional(),
        rightReactivity: z.string().optional(),
    })
        .optional(),
    vision: visionRefined.optional(),
    hearing: hearingRefined.optional(),
    entMouth: z
        .object({
        wnl: z.boolean().optional(),
        details: z.array(z.string()).optional(),
    })
        .refine((d) => d.wnl || !!d.details?.length, wnlMessage)
        .optional(),
});
export const baseMentalStatusSchema = z.object({
    wnl: z.boolean().optional(),
    alertness: z.string().optional(),
    orientedToPerson: z.string().optional(),
    orientedToPlace: z.string().optional(),
    orientedToPurpose: z.string().optional(),
    orientedToTime: z.string().optional(),
    status: z.array(z.string()).optional(),
});
